// The newsroom's reading of a headline: the market families its words name (atlas-geo's headline
// table), the underlyings it names outright, the place it is set in and the exposure region that
// place falls in, and whether it moves a whole market. Signatures are the contract.
//
// Every table is compiled once, at load. Runs in the browser, at the edge and in Node: no DOM, no
// Node APIs, no dependencies.

import type { AssetClass, Tags } from "./types.ts";
import { familiesIn } from "./families.ts";
import { placeIn } from "./places.ts";
import { maskStops } from "./stops.ts";
import { aliasesOf as aliases, canonical as canon, classOf as cls } from "./symbols.ts";
import { asciiLower, clean, shouted } from "./text.ts";
import { tickersIn } from "./tickers.ts";
import { isCryptoWide, isMacro } from "./wide.ts";

export type * from "./types.ts";
export { placeInText } from "./places.ts";
export { REGIONS, regionAt } from "./regions.ts";
export { tickerMentions } from "./tickers.ts";

/** Everything the newsroom reads in one headline. */
export function tagHeadline(text: string): Tags {
  const plain = clean(text);
  const masked = maskStops(plain);
  const reading = { text: masked, lower: asciiLower(masked), shout: shouted(plain) };
  const families = familiesIn(masked);
  const tickers = tickersIn(reading);
  const place = placeIn(reading);
  const underlyings: string[] = [];
  const add = (sym: string): void => {
    const c = canon(sym);
    if (!underlyings.includes(c)) underlyings.push(c);
  };
  tickers.forEach(add);
  for (const family of families) family.underlyings.forEach(add);
  place?.region?.syms.forEach(add);
  return { families, tickers, place, underlyings, macro: isMacro(masked), cryptoWide: isCryptoWide(masked) };
}

/** The canonical name of an underlying: "GOLD", "PAXG" and "XAU" are all "XAU". */
export function canonical(sym: string): string {
  return canon(sym);
}

/** Every name the underlying goes by, canonical first. */
export function aliasesOf(sym: string): string[] {
  return aliases(sym);
}

/** The asset class of an underlying, when known. */
export function classOf(sym: string): AssetClass | null {
  return cls(sym);
}
